import { useInfiniteQuery } from '@tanstack/react-query';
import { dinheiro } from '../agenda/formatos.js';
import { mensagemDoErro } from '../api/cliente.js';
import { estoqueApi } from '../api/estoque.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Erro, EsqueletoDeTabela } from '../ui/estados.jsx';
import { Botao, Card, Status, Tabela, Vazio } from '../ui/primitivos.jsx';
import { EstornoDaEntrada } from './EstornoDaEntrada.jsx';
import { diaDoEstorno, entradaComCusto, quandoEntrou, resumoDosItens, situacaoDaEntrada } from './formatos.js';

/** Linhas por página; "Carregar mais" traz a seguinte. */
const ENTRADAS_POR_PAGINA = 25;

/**
 * A aba **Entradas** de `#v-estoque`: o histórico de compras, da mais recente
 * para trás, com os produtos de cada uma.
 *
 * As colunas de custo só aparecem quando o servidor mandou o custo. Não é a
 * tela escondendo: o dado NÃO VEIO — a projeção tirou `custo_total` e
 * `custo_unitario` do `SELECT` —, e o cabeçalho some junto, porque uma coluna
 * de traços ensina a pessoa que o sistema está quebrado, e não que ela não
 * tem permissão. A situação financeira (o lançamento da saída) vem para todo
 * mundo.
 *
 * Ver: Arena_backend/Docs/plano/13-estoque-comandas.md
 */
export function AbaDeEntradas({ onRegistrar }: { onRegistrar?: (() => void) | undefined }) {
  const { pode } = useSessao();
  const podeEstornar = pode('estoque', 'estornar');
  const entradas = useInfiniteQuery({
    queryKey: ['estoque', 'entradas'],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      estoqueApi.entradas({
        limite: ENTRADAS_POR_PAGINA,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (ultima) => ultima.proximoCursor ?? undefined,
  });

  const itens = entradas.data?.pages.flatMap((pagina) => pagina.itens) ?? [];
  const comCusto = entradaComCusto(itens);

  return (
    <Card titulo="Entradas de mercadoria">
      {entradas.isPending && <EsqueletoDeTabela colunas={6} />}
      {entradas.isError && (
        <Erro
          detalhe={mensagemDoErro(entradas.error)}
          onTentarDeNovo={() => void entradas.refetch()}
        />
      )}
      {entradas.data && itens.length === 0 && (
        <Vazio
          acao={
            onRegistrar ? (
              <Botao pequeno onClick={onRegistrar}>
                Registrar a primeira
              </Botao>
            ) : undefined
          }
        >
          Nenhuma entrada de mercadoria registrada.
        </Vazio>
      )}
      {itens.length > 0 && (
        <>
          <Tabela
            colunas={[
              'Data',
              'Fornecedor',
              'Nota',
              'Itens',
              ...(comCusto ? ['Custo total'] : []),
              'Situação',
              ...(podeEstornar ? [<span className="sr-only">Ações</span>] : []),
            ]}
          >
            {itens.map((entrada) => {
              const { linhas, restantes } = resumoDosItens(entrada.itens);
              const situacao = situacaoDaEntrada(entrada);
              return (
                <tr key={entrada.id} style={entrada.estornadaEm ? { opacity: 0.6 } : undefined}>
                  <td>{quandoEntrou(entrada.criadaEm)}</td>
                  <td>{entrada.fornecedor.nome}</td>
                  <td>{entrada.nota ?? '—'}</td>
                  <td>
                    {linhas.map((linha, indice) => (
                      <div key={indice}>{linha}</div>
                    ))}
                    {restantes > 0 && (
                      <small style={{ color: 'var(--dim)' }}>
                        e mais {restantes} {restantes === 1 ? 'produto' : 'produtos'}
                      </small>
                    )}
                  </td>
                  {comCusto && (
                    <td className="num">
                      {entrada.custoTotal === undefined ? '—' : dinheiro(entrada.custoTotal)}
                    </td>
                  )}
                  <td>
                    <Status situacao={situacao.situacao}>{situacao.rotulo}</Status>
                    {entrada.estornadaEm && (
                      <div>
                        <small style={{ color: 'var(--dim)' }}>
                          estornada em {diaDoEstorno(entrada.estornadaEm)}
                        </small>
                      </div>
                    )}
                  </td>
                  {podeEstornar && (
                    <td>{!entrada.estornadaEm && <EstornoDaEntrada entradaId={entrada.id} />}</td>
                  )}
                </tr>
              );
            })}
          </Tabela>
          {entradas.hasNextPage && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '14px 0 2px' }}>
              <Botao
                variante="contorno"
                pequeno
                onClick={() => void entradas.fetchNextPage()}
                disabled={entradas.isFetchingNextPage}
              >
                {entradas.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
              </Botao>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
